import type { ComponentProps } from 'react';
import type React from 'react';
import { useEffect } from 'react';

import { parseHtml } from '@change-corgi/core-react/html';

type Props = {
	html: string;
	aspectRatio?: number | string;
};

const EmbeddedMediaInner: React.FC<Props & ComponentProps<'div'>> = ({ html, aspectRatio, ...rest }) => {
	return (
		<div
			style={
				{
					'--embedded-media-aspect-ratio': aspectRatio,
				} as React.CSSProperties
			}
			className="[&_iframe]:aspect-(--embedded-media-aspect-ratio) [&_iframe]:h-auto"
			suppressHydrationWarning
			{...rest}
		>
			{parseHtml(html, {
				allowedTags: ['iframe'],
				allowedAttrs: [
					'scrolling',
					'frameborder',
					'allowfullscreen',
					'allow',
					// https://iframely.com/docs/lazy-load#what-you-get
					'data-iframely-url',
					'data-img',
				],
				suppressHydrationWarning: true,
			})}
		</div>
	);
};

const SCRIPT_ID = 'iframely-embed-script';

export const EmbeddedMedia: React.FC<Props & ComponentProps<'div'>> = (props) => {
	const { html } = props;
	const accessibleHtml = html.replace(/<iframe\b/gi, '<iframe aria-label="Media"');

	useEffect(() => {
		if (document.getElementById(SCRIPT_ID)) return;
		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.async = true;
		script.setAttribute('charset', 'utf-8');
		script.src = 'https://iframely.net/embed.js';
		document.head.appendChild(script);
	}, []);

	return <EmbeddedMediaInner {...props} html={accessibleHtml} />;
};
